import axios from "const/axios";
import { API_URL } from "const/constants";
import { fetchModulePayments } from "./accountsApi";
import { enrichPaymentsFromNotes } from "./modulePaymentNoteUtils";
import { calculateRowValues } from "./accountUtils";

/**
 * Re-prices a module row against the payments just loaded for it. Delegates to calculateRowValues,
 * the mirror of the server's ModuleEntryFinancials, rather than repeating the arithmetic: the local
 * copy this replaced left out the MARA cost the row carries in `agreed`, counted commission and
 * bonus that were never claimed, and ignored the insurance month ledger, so a row's profit changed
 * simply because its payments had been loaded.
 */
export const priceModuleRowWithPayments = (row, totalPaid) => {
  const priced = calculateRowValues({ ...row, paidAmount: String(totalPaid) });
  return {
    ...row,
    paidAmount: String(totalPaid),
    dueAmount: String(priced.dueAmount),
    profitLoss: String(priced.profitLoss),
  };
};

export const isLinkedModuleRow = (row) =>
  Boolean(row?.linkedModuleType && row?.linkedModuleId != null);

/**
 * A custom entry: linked, but to itself. It uses the module payment flow like any other linked row,
 * yet its money is edited here rather than on a module screen — so anything that means "this row is
 * owned elsewhere, leave it alone" has to ask this, not isLinkedModuleRow.
 */
export const isOtherEntryRow = (row) => row?.linkedModuleType === "OTHER";

/** Linked to a record on another module's screen, which is the only place its charges can change. */
export const isExternalModuleRow = (row) => isLinkedModuleRow(row) && !isOtherEntryRow(row);

export const netVerifiedFromTransactions = (transactions) =>
  (transactions || [])
    .filter((p) => p.status === "VERIFIED")
    .reduce((sum, p) => {
      const amt = Number(p.amount) || 0;
      return sum + (p.entryType === "REFUND" ? -amt : amt);
    }, 0);

export async function loadLinkedModulePaymentsForCad(cadId) {
  if (!cadId) return [];
  const { data } = await axios.get(
    `${API_URL}/accounts/client-account-details/${cadId}/payment-transactions`
  );
  return data?.data ?? [];
}

/** Persist a row note to the API (Client Accounts has no autosave). */
export async function persistAccountDetailNote(cadId, noteText) {
  if (!cadId || !noteText?.trim()) return null;
  const form = new FormData();
  form.append("parentId", cadId);
  form.append("parentType", "ACCOUNT_DETAIL");
  form.append("noteText", noteText);
  const { data } = await axios.post(`${API_URL}/notes`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data?.data ?? null;
}

export const normalizeModulePayments = (list) =>
  (list || []).map((p) => ({
    id: p.uuid,
    amount: p.amount,
    date: p.date,
    reference: p.reference,
    createdAt: p.createdAt,
  }));

export async function loadModulePaymentsForCad(cadId, rowNotes = [], row = null) {
  if (!cadId) return [];
  if (row && isLinkedModuleRow(row)) {
    return loadLinkedModulePaymentsForCad(cadId);
  }
  const list = await fetchModulePayments(cadId);
  return enrichPaymentsFromNotes(normalizeModulePayments(list), rowNotes);
}

/** Load payments for one row into modulePaymentsMap and optional account-details paidAmount. */
export async function hydrateModulePaymentsForRow(row, setModulePaymentsMap, setAccountDetails) {
  if (!row?.id || row.module === "Admission") return [];
  try {
    const payments = await loadModulePaymentsForCad(row.id, row.notes || [], row);
    setModulePaymentsMap((prev) => ({ ...prev, [row.id]: payments }));
    if (setAccountDetails) {
      const totalPaid = isLinkedModuleRow(row)
        ? netVerifiedFromTransactions(payments)
        : payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      setAccountDetails((prev) =>
        (prev || []).map((r) => {
          if (r.id !== row.id) return r;

          return priceModuleRowWithPayments(r, totalPaid);
        })
      );
    }
    return payments;
  } catch {
    return [];
  }
}

/** Prefetch payment lists for badge counts and sync paidAmount/dueAmount. */
export async function prefetchModulePaymentsMap(
  accountDetails,
  setModulePaymentsMap,
  setAccountDetails
) {
  const rows = (accountDetails || []).filter((r) => r.id && r.module !== "Admission");
  if (!rows.length) return;
  const pairs = await Promise.all(
    rows.map(async (row) => {
      try {
        const payments = await loadModulePaymentsForCad(row.id, row.notes || [], row);
        return [row.id, payments, row];
      } catch {
        return [row.id, null, row];
      }
    })
  );
  setModulePaymentsMap((prev) => {
    const next = { ...prev };
    for (const [cadId, payments] of pairs) {
      next[cadId] = payments !== null ? payments : prev[cadId] ?? [];
    }
    return next;
  });
  if (setAccountDetails) {
    setAccountDetails((prev) =>
      (prev || []).map((r) => {
        if (r.module === "Admission") return r;
        const match = pairs.find(([cadId, payments]) => cadId === r.id && payments !== null);
        if (!match) return r;
        const payments = match[1];
        const totalPaid = isLinkedModuleRow(r)
          ? netVerifiedFromTransactions(payments)
          : payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
        return priceModuleRowWithPayments(r, totalPaid);
      })
    );
  }
}
